/**
 * useImageOptimization - Handles image optimization (all or per-channel).
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import type { UseQueryResult } from '@tanstack/react-query';

export function useImageOptimization(
  productId: string,
  assetsQuery: UseQueryResult,
) {
  const [optimizing, setOptimizing] = useState(false);
  const [optimizingChannel, setOptimizingChannel] = useState<string | null>(null);

  const handleOptimizeImages = useCallback(async (channelId?: string) => {
    if (channelId) { setOptimizingChannel(channelId); }
    else { setOptimizing(true); }

    try {
      const response = await fetch(`/api/v1/${productId}/images/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ channel_id: channelId || null }),
      });
      const result = await response.json();
      if (result.success) {
        toast.success(i18n.t('toast.success.imagesOptimized', { optimized: result.optimized_count, total: result.total_images }));
        if (result.failed_count > 0) {
          toast.warning(`${result.failed_count} Bilder konnten nicht optimiert werden`);
        }
        assetsQuery.refetch();
      } else {
        toast.error(result.error || 'Optimierung fehlgeschlagen');
      }
    } catch (error) {
      toast.error(i18n.t('toast.error.optimizeImages'));
      console.error('Optimize error:', error);
    } finally {
      if (channelId) { setOptimizingChannel(null); }
      else { setOptimizing(false); }
    }
  }, [productId, assetsQuery]);

  return { optimizing, optimizingChannel, handleOptimizeImages };
}
